import type { ComposerCheckpoint } from './workspace-composer-checkpoint';

export type WorkspaceContextKind = 'system' | 'project';

export type WorkspacePreparedDraft = { id: string; question: string; interactionId?: string; contextKey?: string; executionId?: string; mode?: 'read' | 'action'; checkpoint?: ComposerCheckpoint };

/** Reopening currently restores text only; never silently discard saved files. */
export function workspaceDraftAttachmentNotice(executionAttachments: unknown, savedAttachments?: unknown): string | null {
  for (const attachments of [executionAttachments, savedAttachments]) {
    if (attachments === undefined || attachments === null) continue;
    if (!Array.isArray(attachments)) return 'Не удалось проверить вложения исходного черновика. Откройте исходный execution в истории перед повторной подготовкой.';
    if (attachments.length > 0) return 'В исходном черновике есть вложения. Composer после перезагрузки не восстанавливает выбранные файлы и их ключи загрузки. Откройте исходный execution в истории и сверьте вложения перед новой загрузкой: повторный выбор файла может создать копию.';
  }
  return null;
}

export function workspaceClaimLabel(kind: string): string {
  return ({ unit: 'Объект', check_in: 'Заезд', check_out: 'Выезд', monthly_rent: 'Месячная цена',
    split_amount: 'Доля аренды', deposit_amount: 'Депозит', deposit_holder: 'Держатель депозита',
    commission_amount: 'Отдельная комиссия', agent_name: 'Агент', guest_name: 'Гость',
    guest_contact: 'Контакт гостя', guest_count: 'Число гостей', payment_type: 'Тип операции',
    payment_amount: 'Сумма платежа', other_number: 'Другое число' } as Record<string, string>)[kind] ?? 'Упоминание';
}

export type WorkspaceLink = {
  label: string;
  href: string;
};

export type WorkspaceContext = {
  key: string;
  title: string;
  kind: WorkspaceContextKind;
  description?: string | null;
  icon: string;
  links: WorkspaceLink[];
  readSources: string[];
  allowedCapabilities: string[];
  position?: number;
  project?: {
    id: string;
    open_tasks: number;
    total_tasks: number;
  };
};

export type WorkspaceExecution = {
  execution_id: string;
  capability_id: string;
  phase: string;
  run_id: string | null;
  created_at: string;
  status?: string;
  attempt_numbers?: number[] | null;
  business_verified?: boolean;
  approval_id?: string | null;
  run_state?: string | null;
  live_error?: string | null;
  result?: WorkspaceContextReadResult | null;
  draft_summary?: {
    intent: string;
    readiness: 'needs_clarification' | 'prepared';
    can_execute: boolean;
    business_written: boolean;
    executor_status: string | null;
    fields: Record<string, unknown>;
    missing_fields: string[];
    related_actions: Array<{ intent: string; label: string; status: string }>;
  };
  interaction_id?: string;
  source_text?: string;
};


export type WorkspaceVisibleExecution = {
  execution: WorkspaceExecution;
  count: number;
};

export function workspaceVisibleExecutions(
  executions: WorkspaceExecution[],
): WorkspaceVisibleExecution[] {
  // Keep every execution and phase; a phase is not a retry.
  return executions.map((execution) => ({ execution, count: 1 }));
}

export function workspacePhaseLabel(phase: string): string {
  return ({
    read: 'Чтение контекста',
    draft: 'Подготовка',
    'suggested-draft': 'Подготовка предложения',
    'draft-selection': 'Выбор действия',
    entry: 'Запрос принят',
    run: 'Выполнение',
    apply: 'Применение',
  } as Record<string, string>)[phase] ?? 'Этап обработки';
}

export type WorkspaceSuggestedAction = {
  label: string;
  capability_id: string;
  input_preview: Record<string, unknown>;
};

export type WorkspaceMemorySuggestionProposal = {
  should_remember: boolean;
  proposed_memory: string;
  reason: string;
};

export type WorkspaceContextReadResult = {
  answer_md: string;
  evidence: Array<{
    type: string;
    id: string;
    label: string;
  }>;
  suggested_actions: WorkspaceSuggestedAction[];
  missing_context: string[];
  memory_suggestion?: WorkspaceMemorySuggestionProposal;
};

const PREPARABLE_SUGGESTED_CAPABILITIES = new Set([
  'ops.action.draft',
  'ops.booking.apply',
  'ops.booking_payment.apply',
  'ops.tm30.apply',
  'ops.transfer.apply',
  'ops.deposit.apply',
  'ops.payment.allocate',
  'ops.owner_expense.apply',
  'ops.tech_request.apply',
]);

export function workspaceSuggestedActionCanPrepare(
  allowedCapabilities: string[] | undefined,
  capabilityId: string,
): boolean {
  if (!allowedCapabilities) return false;
  return (
    PREPARABLE_SUGGESTED_CAPABILITIES.has(capabilityId) &&
    allowedCapabilities.includes(capabilityId) &&
    allowedCapabilities.includes('ops.action.draft')
  );
}

export type WorkspaceAttentionItem = {
  id: string;
  kind: 'execution_failed' | 'missing_context' | 'suggested_action';
  title: string;
  detail: string | null;
  interaction_id: string;
  execution_id: string;
  capability_id: string | null;
  input_preview: Record<string, unknown> | null;
  created_at: string;
};

export type WorkspaceInteraction = {
  id: string;
  context_key: string;
  source_text: string;
  attachments: unknown[];
  created_by: string | null;
  created_at: string;
  executions: WorkspaceExecution[];
};

export type WorkspaceTimelineItem = {
  id: string;
  kind: 'interaction' | 'execution' | 'state' | 'approval' | 'run' | 'evidence' | 'result' | 'closed';
  timestamp: string;
  title: string;
  detail: string | null;
  execution_id: string | null;
  run_id: string | null;
  approval_id: string | null;
  source: 'jack-os' | 'control-plane';
};

export type WorkspaceTimeline = {
  interaction: {
    id: string;
    context_key: string;
    source_text: string;
    created_at: string;
  };
  items: WorkspaceTimelineItem[];
  degraded: {
    control_plane: boolean;
  };
};

export type WorkspaceMemorySuggestion = {
  id: string;
  context_key: string;
  interaction_id: string;
  execution_id: string;
  base_memory_md: string;
  proposed_memory_md: string;
  reason: string;
  status: 'pending';
  created_at: string;
  resolved_at: null;
};

export type WorkspaceOverviewMetric = {
  label: string;
  value: string;
};

export type WorkspaceOverviewItem = {
  title: string;
  detail: string | null;
  href: string | null;
  tone: 'normal' | 'attention';
};

export type WorkspaceOverview = {
  status: 'ready' | 'partial' | 'unavailable';
  updated_at: string | null;
  metrics: WorkspaceOverviewMetric[];
  items: WorkspaceOverviewItem[];
  note: string | null;
};

export type WorkspaceReadModel = {
  context: WorkspaceContext;
  overview?: WorkspaceOverview;
  state: {
    memory_note_id: string | null;
    memory_md: string;
    updated_at: string | null;
    activity_count: number;
    last_interaction_at: string | null;
    project: WorkspaceContext['project'] | null;
  };
  attention: WorkspaceAttentionItem[];
  running: WorkspaceExecution[];
  approvals: WorkspaceExecution[];
  memory_suggestions: WorkspaceMemorySuggestion[];
  related: WorkspaceLink[];
  links: WorkspaceLink[];
  interactions: WorkspaceInteraction[];
  degraded: {
    control_plane: boolean;
  };
};

const ENGINEERING_INTERACTION_RE = /^(?:smoke[-_:]|test[-_:]|stage\d+[-_:])/i;
const CONTEXT_READ_RE = /^(?:что|кто|где|когда|сколько|како(?:й|е|ие|ая)|есть ли|покажи|перечисли)(?=$|\s|[?.!,;:])/u;

export function workspaceLooksLikeContextReadText(text: string): boolean {
  const value = text.trim().toLocaleLowerCase('ru-RU').replace(/ё/g, 'е');
  return CONTEXT_READ_RE.test(value);
}

export function workspaceIsEngineeringInteraction(
  interaction: Pick<WorkspaceInteraction, 'source_text'>,
): boolean {
  return ENGINEERING_INTERACTION_RE.test(interaction.source_text.trim());
}

export function workspaceIsLegacyMisroutedReadInteraction(
  interaction: Pick<WorkspaceInteraction, 'source_text' | 'executions'>,
): boolean {
  if (!workspaceLooksLikeContextReadText(interaction.source_text) || interaction.executions.length === 0) return false;
  if (interaction.executions.some((execution) => execution.capability_id === 'workspace.context.read')) return false;
  return interaction.executions.every((execution) => execution.capability_id === 'ops.action.draft');
}

export function workspaceVisibleInteractions(
  interactions: WorkspaceInteraction[],
): WorkspaceInteraction[] {
  return interactions.filter(
    (interaction) =>
      !workspaceIsEngineeringInteraction(interaction) &&
      !workspaceIsLegacyMisroutedReadInteraction(interaction),
  );
}

export function formatWorkspaceTime(value: string | null | undefined): string {
  if (!value) return '';
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', {
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function workspaceStatusLabel(status: string | undefined): string {
  if (!status) return 'Статус недоступен';
  const labels: Record<string, string> = {
    queued: 'В очереди',
    running: 'Выполняется',
    pending_approval: 'Нужно подтверждение',
    succeeded: 'Выполнено',
    failed: 'Ошибка',
    cancelled: 'Отменено',
    rejected: 'Отклонено',
  };
  return labels[status] ?? 'Статус пока не известен';
}

const CAPABILITY_LABELS: Record<string, string> = {
  'repo.status.read': 'Проверка репозитория',
  'space.job.run': 'Задание в Space',
  'ops.goal.track': 'Отслеживание цели',
  'ops.media.review': 'Проверка медиа',
  'ops.run.external_completion': 'Подтверждение внешнего выполнения',
  'ops.run.staff_action': 'Действие сотрудника',
  'ops.hermes.result_reply': 'Ответ по результату Hermes',
  'ops.tech_request.draft': 'Черновик технической заявки',
  'workspace.context.read': 'Ответ по контексту',
  'ops.action.draft': 'Подготовлено действие',
  'ops.booking.apply': 'Создание брони',
  'ops.booking_payment.apply': 'Платёж по брони',
  'ops.tm30.apply': 'Оформление TM30',
  'ops.transfer.apply': 'Трансфер',
  'ops.deposit.apply': 'Депозит',
  'ops.payment.allocate': 'Распределение платежа',
  'ops.tech_request.apply': 'Техническая заявка',
  'ops.owner_expense.apply': 'Расход владельца',
  'ops.entry.ingest': 'Запрос принят',
  'ops.run.start': 'Выполнение начато',
  'ops.run.guest_reply': 'Ответ гостю',
};

export function workspaceCapabilityLabel(capabilityId: string): string {
  return CAPABILITY_LABELS[capabilityId] ?? capabilityId;
}

export function workspaceRunStateLabel(state: string | null | undefined): string {
  if (!state) return '';
  const labels: Record<string, string> = {
    CREATED: 'Создано',
    RESOLVING: 'Подготовка',
    TICKETED: 'Задача создана',
    ASSIGNED: 'Назначено',
    WAITING_EVIDENCE: 'Ждём результат',
    AWAITING_GUEST_APPROVE: 'Нужно подтверждение',
    BLOCKED: 'Нужна проверка',
    CLOSED: 'Готово',
  };
  return labels[state] ?? 'Статус пока не известен';
}

export type WorkspaceMemorySection = {
  heading: string | null;
  body: string;
};

export type WorkspaceMemoryDiff = {
  added: string[];
  removed: string[];
};

function countLines(value: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const line of value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean)) {
    counts.set(line, (counts.get(line) ?? 0) + 1);
  }
  return counts;
}

export function workspaceMemoryDiff(base: string, proposed: string): WorkspaceMemoryDiff {
  const before = countLines(base);
  const after = countLines(proposed);
  const removed: string[] = [];
  const added: string[] = [];

  for (const [line, count] of before) {
    const delta = count - (after.get(line) ?? 0);
    for (let index = 0; index < delta; index += 1) removed.push(line);
  }
  for (const [line, count] of after) {
    const delta = count - (before.get(line) ?? 0);
    for (let index = 0; index < delta; index += 1) added.push(line);
  }
  return { added, removed };
}

export function workspaceMemorySections(markdown: string): WorkspaceMemorySection[] {
  const text = markdown.trim();
  if (!text) return [];

  const sections: WorkspaceMemorySection[] = [];
  let heading: string | null = null;
  let body: string[] = [];

  const flush = () => {
    const content = body.join('\n').trim();
    if (heading || content) sections.push({ heading, body: content });
    body = [];
  };

  for (const line of text.split(/\r?\n/)) {
    const match = line.match(/^#{1,6}\s+(.+?)\s*$/);
    if (match) {
      flush();
      heading = match[1]?.trim() || null;
      continue;
    }
    body.push(line);
  }
  flush();

  return sections;
}

const CONTEXT_HINTS: Record<string, string> = {
  today: 'Сводка текущей работы: операции, подтверждения и последние результаты.',
  rent: 'Аренда, объекты, брони и платежи в одном рабочем контексте.',
  leads: 'Лиды, спрос, подборки и следующие действия по заявкам.',
  operations: 'Runs, технические заявки, TM30, transfers и работа сотрудников.',
  finance: 'Кассы, комиссии, депозиты и связанные платежные операции.',
  market: 'Рынок и конкурентная разведка без бизнес-записей.',
  'control-plane': 'Executions, approvals и состояние исполнительного контура.',
};

export function workspaceContextHint(context: WorkspaceContext): string {
  if (context.description?.trim()) return context.description.trim();
  if (context.kind === 'project') return 'Задачи, прогресс и рабочая память проекта.';
  return CONTEXT_HINTS[context.key] ?? 'Рабочий контекст Jack OS.';
}

export function workspaceActivityPeriod(interactions: WorkspaceInteraction[]): string {
  const dates = interactions.map((item) => {
    const value = item.created_at.includes('T') ? item.created_at : `${item.created_at.replace(' ', 'T')}Z`;
    return new Date(value);
  }).filter((date) => Number.isFinite(date.getTime())).sort((a, b) => a.getTime() - b.getTime());
  if (!dates.length) return 'Период не указан';
  const format = (date: Date) => date.toLocaleDateString('ru-RU', { timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric' });
  const first = format(dates[0]!);
  const last = format(dates[dates.length - 1]!);
  return first === last ? first : `${first} — ${last}`;
}

/** Повторный вход в композитор сохраняет контекст и неподготовленный черновик. */
export function workspaceComposerHref(pathname: string, search: string): string {
  const params = new URLSearchParams(pathname.replace(/\/+$/, '') === '/workspace' ? search : '');
  params.set('compose', '1');
  return '/workspace?' + params.toString();
}

export function workspaceAttemptsLabel(numbers: number[] | null | undefined): string {
  return numbers?.length ? `Попытки исполнителя CP: ${numbers.join(', ')}` : 'Попытки исполнителя CP: не указаны';
}
